import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchArticles } from "@/lib/queries";
import { usePaywallFilter } from "@/hooks/use-paywall-filter";
import { isTopic, TOPIC_LABEL, TOPIC_TAGLINE, TOPIC_SEO_TEXT, type Topic } from "@/lib/topics";
import { ArticleListRow } from "@/components/ArticleListRow";
import { PaywallBadge } from "@/components/PaywallBadge";
import { detectFormat, formatLabel } from "@/lib/format-detector";
import { renderWithWikiLinks } from "@/lib/wiki-link";
import { useReadArticles } from "@/hooks/use-read-articles";
import { formatArticleDate, formatUpdatedAt } from "@/lib/format-date";
import { SeoNote } from "@/components/SeoNote";

export const Route = createFileRoute("/emne/$topic")({
  beforeLoad: ({ params }) => {
    if (!isTopic(params.topic)) throw notFound();
  },
  loader: async ({ params, context }) => {
    const t = params.topic as Topic;
    const articles = await context.queryClient.ensureQueryData({
      queryKey: ["articles", t, true],
      queryFn: () => fetchArticles({ topic: t, hidePaywall: true }),
    });
    return { articles };
  },

  head: ({ params }) => {
    const t = params.topic as Topic;
    const label = isTopic(params.topic) ? TOPIC_LABEL[t] : "Emne";
    const tagline = isTopic(params.topic) ? TOPIC_TAGLINE[t] : "";
    const title = `${label} – Flâneriet`;
    const desc = tagline || `Kuraterede tekster om ${label.toLowerCase()} – samlet på Flâneriet.`;
    const url = `https://flaneriet.online/emne/${params.topic}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: TopicPage,
});


function TopicPage() {
  const { topic } = Route.useParams();
  const t = topic as Topic;
  const { hidePaywall } = usePaywallFilter();
  const { markRead, isRead } = useReadArticles();
  const { data, isLoading } = useQuery({
    queryKey: ["articles", t, hidePaywall],
    queryFn: () => fetchArticles({ topic: t, hidePaywall }),
  });

  const list = data ?? [];
  const latest = list[0];
  const updatedLabel = formatUpdatedAt(latest?.created_at);

  return (
    <div>
      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2.5">Sektion</p>
            <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">
              {TOPIC_LABEL[t]}
            </h1>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 py-6 flex-wrap">
        <p className="meta italic">
          {TOPIC_TAGLINE[t]}
          {updatedLabel && <> · {updatedLabel}</>}
        </p>
      </div>

      {isLoading && <p className="meta italic py-16 text-center">Henter …</p>}

      {!isLoading && list.length === 0 && (
        <p className="meta italic py-24 text-center">Endnu intet under {TOPIC_LABEL[t]}. Kom tilbage om lidt.</p>
      )}

      {list.length > 0 && (
        <section className="min-w-0">
          {list.map((a: any, i: number) => {
            const fmt = detectFormat(a.original_url, a.title);
            const kicker = (
              <span className="inline-flex items-center gap-2">
                {TOPIC_LABEL[a.topic as Topic]}
                {fmt !== "article" ? <span className="text-accent-dark not-italic"> · {formatLabel(fmt)}</span> : null}
                <PaywallBadge paywall={a.paywall} />
              </span>
            );
            const sourceDomain = (() => {
              try { return new URL(a.original_url).hostname.replace(/^www\./, ""); } catch { return a.source ?? ""; }
            })();
            const sourceMeta = (
              <>
                {a.source ?? sourceDomain}
                {a.created_at ? ` · ${formatArticleDate(a.created_at)}` : ""}
              </>
            );
            return (
              <ArticleListRow
                key={a.id}
                id={a.id}
                kicker={kicker}
                title={a.title}
                href={a.original_url}
                imageUrl={a.image_url ?? null}
                reason={a.selection_reason ? renderWithWikiLinks(a.selection_reason) : (a.summary ?? undefined)}
                sourceMeta={sourceMeta}
                outLabel={`Læs hos ${a.source ?? sourceDomain}`}
                onOpen={() => markRead(a.id)}
                variant={i === 0 ? "lead" : "row"}
                unread={!isRead(a.id)}
              />
            );
          })}
        </section>
      )}

      <SeoNote eyebrow={`Om ${TOPIC_LABEL[t].toLowerCase()}`}>
        <p>{TOPIC_SEO_TEXT[t]}</p>
      </SeoNote>
    </div>
  );
}
